import React, { useState, useEffect } from 'react';
import { useStore } from 'effector-react';
import PlatformFilterButton from '@app/components/PlatformFilterButton';
import PlatformListFilter from '@app/components/PlatformListFilter';
import { Radio, Position } from '@blueprintjs/core';
import { $isQueryHydrationFinished } from '@app/containers/pages/CoreUsers/store/states';
import {
    initialPlatformCoreUserFilterData,
    platformCoreUserFiltersInformation,
} from '@app/containers/pages/CoreUsers/consts';
import { PlatformCoreUserFilterType } from '@app/containers/pages/CoreUsers/enums';
import { setPlatformCoreUserFilterData } from '@app/containers/pages/CoreUsers/store/events';
import { PlatformCoreUserFilterData } from '@app/containers/pages/CoreUsers/types';

export interface CoreUserArchivedPlatformFilterProps {
    defaultIsOpen?: boolean;
    platformCoreUserFilterData: PlatformCoreUserFilterData;
    onRemove: () => void;
}

export type Props = CoreUserArchivedPlatformFilterProps;

const CoreUserArchivedPlatformFilter: React.FC<Props> = ({
    defaultIsOpen = false,
    platformCoreUserFilterData,
    onRemove,
}) => {
    const isQueryHydrationFinished = useStore($isQueryHydrationFinished);

    const [isOpen, setIsOpen] = useState(defaultIsOpen);
    const [isOnlyArchivedVisible, setIsOnlyArchivedVisible] = useState<NonNullable<
        PlatformCoreUserFilterData['isArchived']
    > | null>(platformCoreUserFilterData.isArchived);

    useEffect(() => {
        if (isQueryHydrationFinished) {
            setIsOnlyArchivedVisible(platformCoreUserFilterData.isArchived);
        }

        // eslint-disable-next-line
    }, [isQueryHydrationFinished]);

    useEffect(() => {
        if (platformCoreUserFilterData.isArchived === null) {
            setIsOnlyArchivedVisible(null);
        }
    }, [platformCoreUserFilterData.isArchived]);

    const getFilterTagLabel = () => {
        if (isOnlyArchivedVisible === null) {
            return '';
        }

        if (isOnlyArchivedVisible) {
            return 'Only archived accounts';
        } else {
            return 'Only active accounts';
        }
    };

    const getFilterTagTitle = () => {
        if (isOnlyArchivedVisible === null) {
            return 'Archived';
        }

        return 'Archived:';
    };

    const handleRemoveFilter = () => {
        setIsOnlyArchivedVisible(null);
        setPlatformCoreUserFilterData({
            ...platformCoreUserFilterData,
            isArchived: initialPlatformCoreUserFilterData.isArchived,
        });
        setIsOpen(false);
        if (!platformCoreUserFiltersInformation[PlatformCoreUserFilterType.CORE_USER_ARCHIVED].isPinned) {
            // Platform core user archived filter is NOT pinned, so it can be removed
            onRemove();
        }
    };

    const handleApplyFilter = () => {
        if (platformCoreUserFilterData.isArchived !== isOnlyArchivedVisible) {
            setPlatformCoreUserFilterData({ ...platformCoreUserFilterData, isArchived: isOnlyArchivedVisible });
        }
    };

    const handleCloseFilter = () => {
        setIsOpen(false);

        if (platformCoreUserFiltersInformation[PlatformCoreUserFilterType.CORE_USER_ARCHIVED].isPinned) {
            return;
        }

        if (isOnlyArchivedVisible === null) {
            onRemove();
        }
    };

    const renderRow = (key: number) => {
        return (
            <li key={key}>
                <Radio
                    className="mb-1"
                    checked={isOnlyArchivedVisible !== null && !isOnlyArchivedVisible}
                    label="Only active accounts"
                    onChange={() => setIsOnlyArchivedVisible(false)}
                />

                <Radio
                    checked={isOnlyArchivedVisible !== null && isOnlyArchivedVisible}
                    label="Only archived accounts"
                    onChange={() => setIsOnlyArchivedVisible(true)}
                />
            </li>
        );
    };

    return (
        <PlatformListFilter<number>
            removable
            isOpen={isOpen}
            title="Archived"
            position={Position.BOTTOM_LEFT}
            list={[1]}
            itemRenderer={renderRow}
            onApply={handleApplyFilter}
            onClose={handleCloseFilter}
            onRemove={handleRemoveFilter}
        >
            <PlatformFilterButton
                icon={platformCoreUserFiltersInformation[PlatformCoreUserFilterType.CORE_USER_ARCHIVED].icon}
                onClick={() => setIsOpen(!isOpen)}
                label={getFilterTagLabel()}
                title={getFilterTagTitle()}
            />
        </PlatformListFilter>
    );
};

export default CoreUserArchivedPlatformFilter;
